import React, { useEffect, useState, useCallback } from 'react';
import {
  Users, UserPlus, Shield, User, Wrench,
  CheckCircle, XCircle, Trash2,
  X, Building2, MapPin, ChevronDown, ChevronRight,
  AlertTriangle, Lock, Clock, Send, Mail, ChevronsUpDown, Tablet, Eye, EyeOff, ShoppingCart
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../api';
import { useAuth } from '../../context/AuthContext';
import { SidePanel } from '../../components/SidePanel';

// ─── Constantes ───────────────────────────────────────────────────────────────

const ROLES = {
  super_admin: { label: 'Super Admin', icon: Shield, color: '#7c3aed' },
  admin_org: { label: 'Admin', icon: Shield, color: '#6366f1' },
  technicien: { label: 'Technicien', icon: Wrench, color: '#0ea5e9' },
  user: { label: 'Utilisateur', icon: User, color: '#64748b' },
};

// ─── Sous-composants utilitaires ─────────────────────────────────────────────

function RoleBadge({ role }) {
  const cfg = ROLES[role] || ROLES.user;
  const Icon = cfg.icon;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: `${cfg.color}18`, color: cfg.color }}>
      <Icon size={10} /> {cfg.label}
    </span>
  );
}

// ─── Panneau de création d'utilisateur ───────────────────────────────────────

function CreateUserPanel({ onClose, onCreated, societes }) {
  const [activeTab, setActiveTab] = useState('profil');
  const [terrainMode, setTerrainMode] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'user' });
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [access, setAccess] = useState([]);
  const [menuRights, setMenuRights] = useState(['dashboard']);
  const [saving, setSaving] = useState(null); // null | 'create' | 'invite'

  // Quand on bascule en mode terrain : pré-cocher le droit terrain
  const toggleTerrainMode = () => {
    const next = !terrainMode;
    setTerrainMode(next);
    if (next) {
      setMenuRights(r => r.includes('terrain_comptages') ? r : [...r, 'terrain_comptages']);
      setActiveTab('profil');
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleCreate = async (sendInvitation) => {
    if (!form.name.trim() || !form.email.trim()) {
      setActiveTab('profil');
      return toast.error('Nom et email requis');
    }
    if (terrainMode && !password.trim()) {
      return toast.error('Le mot de passe est requis pour un compte terrain');
    }
    setSaving(sendInvitation ? 'invite' : 'create');
    try {
      const payload = {
        ...form,
        app_url: window.location.origin,
        send_invitation: terrainMode ? false : sendInvitation,
        ...(terrainMode && password.trim() ? { password: password.trim() } : {}),
      };
      const r = await api.inviteUser(payload);
      const newUser = r.data;
      await Promise.all([
        access.length > 0 ? api.updateUserAccess(newUser.user_id, access) : null,
        menuRights.length > 0 ? api.updateUserMenuRights(newUser.user_id, menuRights) : null,
      ].filter(Boolean));
      onCreated(newUser);
      toast.success(terrainMode
        ? `Compte terrain créé — connexion immédiate possible`
        : sendInvitation
          ? `Invitation envoyée à ${newUser.email}`
          : `Utilisateur créé. Envoyez l'invitation quand vous le souhaitez.`
      );
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(null);
    }
  };

  const TABS = [
    { key: 'profil', label: 'Profil' },
    { key: 'acces', label: 'Accès' },
    { key: 'droits', label: 'Droits Menu' },
  ];
  const inputCls = `w-full px-3 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-200`;
  const inputStyle = { borderColor: 'var(--border-light)', background: 'white', color: 'var(--text-primary)' };
  const isAdminRole = form.role === 'admin_org' || form.role === 'super_admin';

  return (
    <SidePanel
      open
      onOpenChange={open => { if (!open) onClose(); }}
      title="Créer un utilisateur"
      subtitle={terrainMode ? 'Compte terrain — connexion par mot de passe direct' : 'Configurez le profil, les accès et les droits avant d\'envoyer l\'invitation'}
      footer={
        <div className="flex gap-2">
          <button onClick={onClose}
            className="px-4 py-2.5 rounded-lg text-sm font-medium"
            style={{ color: 'var(--text-muted)' }}>
            Annuler
          </button>
          {terrainMode ? (
            <button onClick={() => handleCreate(false)} disabled={!!saving}
              data-testid="create-terrain-btn"
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #7C3AED, #6D28D9)' }}>
              <Tablet size={14} />
              {saving ? 'Création...' : 'Créer le compte terrain'}
            </button>
          ) : (
            <>
              <button onClick={() => handleCreate(false)} disabled={!!saving}
                data-testid="create-user-btn"
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60 border"
                style={{ borderColor: 'var(--primary-solid)', color: 'var(--primary-solid)', background: 'white' }}>
                {saving === 'create' ? 'Création...' : 'Créer sans inviter'}
              </button>
              <button onClick={() => handleCreate(true)} disabled={!!saving}
                data-testid="create-and-invite-btn"
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-60 flex items-center justify-center gap-2"
                style={{ background: 'var(--primary-gradient)' }}>
                <Send size={14} />
                {saving === 'invite' ? 'Envoi...' : 'Créer et inviter'}
              </button>
            </>
          )}
        </div>
      }
    >
      <div className="space-y-4">

        {/* Toggle mode terrain */}
        <button
          type="button"
          onClick={toggleTerrainMode}
          data-testid="toggle-terrain-mode-btn"
          className="w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all"
          style={{
            borderColor: terrainMode ? '#7C3AED' : 'var(--border-light)',
            background: terrainMode ? '#F5F3FF' : 'var(--surface-1)',
          }}>
          <div className="flex items-center gap-2.5">
            <Tablet size={16} style={{ color: terrainMode ? '#7C3AED' : 'var(--text-muted)' }} />
            <div className="text-left">
              <p className="text-sm font-semibold" style={{ color: terrainMode ? '#7C3AED' : 'var(--text-primary)' }}>
                Compte terrain (tablette)
              </p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Mot de passe défini par l'admin, connexion sans email d'invitation
              </p>
            </div>
          </div>
          <div className="w-10 h-5 rounded-full flex-shrink-0 transition-all relative"
            style={{ background: terrainMode ? '#7C3AED' : '#D1D5DB' }}>
            <div className="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all"
              style={{ left: terrainMode ? '22px' : '2px' }} />
          </div>
        </button>

        {/* Onglets */}
        <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'var(--surface-1)' }}>
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className="flex-1 py-2 text-xs font-semibold rounded-lg transition-all"
              style={{
                background: activeTab === tab.key ? 'white' : 'transparent',
                color: activeTab === tab.key ? 'var(--primary-solid)' : 'var(--text-muted)',
                boxShadow: activeTab === tab.key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Onglet Profil */}
        {activeTab === 'profil' && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide"
                style={{ color: 'var(--text-muted)' }}>Nom complet *</label>
              <input data-testid="create-user-name-input" className={inputCls} style={inputStyle}
                value={form.name} onChange={e => set('name', e.target.value)} placeholder="Jean Dupont" autoFocus />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide"
                style={{ color: 'var(--text-muted)' }}>
                Email {terrainMode ? '(optionnel — identifiant de connexion)' : '*'}
              </label>
              <input data-testid="create-user-email-input" className={inputCls} style={inputStyle}
                type="email" value={form.email} onChange={e => set('email', e.target.value)}
                placeholder={terrainMode ? 'terrain@votreentreprise.com' : 'jean@example.com'} />
              {terrainMode && (
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                  Peut être une adresse interne fictive — aucun email ne sera envoyé.
                </p>
              )}
            </div>
            {/* Mot de passe direct — uniquement mode terrain */}
            {terrainMode && (
              <div>
                <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide"
                  style={{ color: 'var(--text-muted)' }}>Mot de passe *</label>
                <div className="relative">
                  <input
                    data-testid="create-terrain-password-input"
                    className={inputCls}
                    style={{ ...inputStyle, paddingRight: '40px' }}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Définir le mot de passe de connexion"
                  />
                  <button type="button" onClick={() => setShowPassword(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1">
                    {showPassword
                      ? <EyeOff size={15} style={{ color: 'var(--text-muted)' }} />
                      : <Eye size={15} style={{ color: 'var(--text-muted)' }} />}
                  </button>
                </div>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                  À communiquer à l'opérateur. Peut être changé depuis "Mes paramètres".
                </p>
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide"
                style={{ color: 'var(--text-muted)' }}>Rôle</label>
              <select data-testid="create-user-role-select" className={`${inputCls} appearance-none`} style={inputStyle}
                value={form.role} onChange={e => set('role', e.target.value)}>
                {Object.entries(ROLES).map(([r, cfg]) => (
                  <option key={r} value={r}>{cfg.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Onglet Accès */}
        {activeTab === 'acces' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
                Accès sociétés & agences
              </p>
              {access.length === 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#fef3c7', color: '#92400e' }}>
                  Accès global
                </span>
              )}
            </div>
            {access.length === 0 && (
              <div className="flex items-start gap-2 p-3 rounded-xl mb-3 text-xs"
                style={{ background: '#fef9ee', border: '1px solid #fde68a', color: '#78350f' }}>
                <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                Sans restriction définie, cet utilisateur aura accès à toutes les sociétés et agences.
              </div>
            )}
            <AccessSection societes={societes} access={access} onChange={setAccess} />
          </div>
        )}

        {/* Onglet Droits Menu */}
        {activeTab === 'droits' && (
          <div>
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--text-muted)' }}>
              Visibilité du menu
            </p>
            {isAdminRole ? (
              <div className="flex items-start gap-2 p-3 rounded-xl text-xs"
                style={{ background: 'var(--primary-50)', border: '1px solid var(--primary-100)', color: 'var(--primary-solid)' }}>
                <Lock size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                Les administrateurs voient toujours tout le menu, quels que soient les droits configurés ici.
              </div>
            ) : (
              <MenuRightsSection menuRights={menuRights} onChange={setMenuRights} />
            )}
          </div>
        )}
      </div>
    </SidePanel>
  );
}

// ─── Section droits d'accès société/agences ───────────────────────────────────

function AccessSection({ societes, access, onChange }) {
  // access = [{ societe_id, agence_ids: [] }]
  const getAccess = (sid) => access.find(a => a.societe_id === sid) || null;
  const hasSociete = (sid) => !!getAccess(sid);
  const hasAgence = (sid, aid) => getAccess(sid)?.agence_ids?.includes(aid) ?? false;

  const toggleSociete = (soc) => {
    if (hasSociete(soc.societe_id)) {
      // Retirer la société et toutes ses agences
      onChange(access.filter(a => a.societe_id !== soc.societe_id));
    } else {
      // Ajouter la société sans agence spécifique (accès global à la société)
      onChange([...access, { societe_id: soc.societe_id, agence_ids: [] }]);
    }
  };

  const toggleAgence = (soc, agc) => {
    const current = getAccess(soc.societe_id);
    if (!current) {
      // Ajouter la société + cette agence
      onChange([...access, { societe_id: soc.societe_id, agence_ids: [agc.agence_id] }]);
    } else {
      const hasIt = current.agence_ids.includes(agc.agence_id);
      const newIds = hasIt
        ? current.agence_ids.filter(id => id !== agc.agence_id)
        : [...current.agence_ids, agc.agence_id];
      onChange(access.map(a =>
        a.societe_id === soc.societe_id ? { ...a, agence_ids: newIds } : a
      ));
    }
  };

  const toggleAllAgences = (soc) => {
    const current = getAccess(soc.societe_id);
    const allIds = (soc.agences || []).map(a => a.agence_id);
    const allChecked = current && allIds.every(id => current.agence_ids.includes(id));

    if (!current) {
      onChange([...access, { societe_id: soc.societe_id, agence_ids: allIds }]);
    } else {
      onChange(access.map(a =>
        a.societe_id === soc.societe_id
          ? { ...a, agence_ids: allChecked ? [] : allIds }
          : a
      ));
    }
  };

  if (!societes?.length) {
    return (
      <div className="py-6 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
        <Building2 size={28} className="mx-auto mb-2 opacity-30" />
        Aucune société dans cette organisation.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {(societes || []).map(soc => {
        const checked = hasSociete(soc.societe_id);
        const current = getAccess(soc.societe_id);
        const agences = soc.agences || [];
        const allAgencesChecked = agences.length > 0
          && agences.every(a => current?.agence_ids?.includes(a.agence_id));

        return (
          <div key={soc.societe_id}
            data-testid={`access-societe-${soc.societe_id}`}
            className="rounded-xl border overflow-hidden"
            style={{ borderColor: checked ? 'var(--primary-100)' : 'var(--border-light)' }}>

            {/* Ligne société */}
            <label className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50/60 select-none"
              style={{ background: checked ? 'var(--primary-50)' : 'white' }}>
              <input
                type="checkbox"
                data-testid={`access-societe-cb-${soc.societe_id}`}
                checked={checked}
                onChange={() => toggleSociete(soc)}
                className="w-4 h-4 rounded"
                style={{ accentColor: 'var(--primary-solid)' }}
              />
              <Building2 size={15} style={{ color: checked ? 'var(--primary-solid)' : 'var(--text-muted)', flexShrink: 0 }} />
              <span className="font-semibold text-sm flex-1"
                style={{ color: checked ? 'var(--primary-solid)' : 'var(--text-primary)' }}>
                {soc.nom}
              </span>
              {checked && agences.length > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full"
                  style={{ background: 'var(--primary-100)', color: 'var(--primary-solid)' }}>
                  {current?.agence_ids?.length === 0 ? 'Toutes les agences' : `${current?.agence_ids?.length} agence${current?.agence_ids?.length > 1 ? 's' : ''}`}
                </span>
              )}
            </label>

            {/* Sous-section agences */}
            {checked && agences.length > 0 && (
              <div className="border-t px-4 py-2 space-y-1"
                style={{ borderColor: 'var(--border-light)', background: 'white' }}>
                {/* Toutes les agences */}
                <label className="flex items-center gap-3 py-1.5 cursor-pointer hover:bg-gray-50/60 rounded-lg px-2 select-none">
                  <input
                    type="checkbox"
                    data-testid={`access-all-agences-${soc.societe_id}`}
                    checked={allAgencesChecked}
                    onChange={() => toggleAllAgences(soc)}
                    className="w-3.5 h-3.5 rounded"
                    style={{ accentColor: 'var(--primary-solid)' }}
                  />
                  <span className="text-xs font-semibold italic" style={{ color: 'var(--text-muted)' }}>
                    Toutes les agences
                  </span>
                </label>
                {/* Agences individuelles */}
                {agences.map(agc => (
                  <label key={agc.agence_id}
                    data-testid={`access-agence-${agc.agence_id}`}
                    className="flex items-center gap-3 py-1.5 cursor-pointer hover:bg-gray-50/60 rounded-lg px-2 select-none">
                    <input
                      type="checkbox"
                      data-testid={`access-agence-cb-${agc.agence_id}`}
                      checked={hasAgence(soc.societe_id, agc.agence_id)}
                      onChange={() => toggleAgence(soc, agc)}
                      className="w-3.5 h-3.5 rounded"
                      style={{ accentColor: 'var(--primary-solid)' }}
                    />
                    <MapPin size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {agc.nom}
                    </span>
                    {agc.ville && (
                      <span className="text-xs ml-auto" style={{ color: 'var(--text-disabled)' }}>
                        {agc.ville}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Structure du menu (miroir de Layout.js navItems) ────────────────────────

const MENU_STRUCTURE = [
  { key: 'dashboard', label: 'Tableau de bord', children: [
    { key: 'dashboard_general',    label: 'Onglet Général' },
    { key: 'dashboard_location',   label: 'Onglet Location' },
    { key: 'dashboard_reparation', label: 'Onglet Réparation' },
  ]},
  {
    key: 'annuaire', label: 'Annuaire', children: [
      { key: 'contacts',              label: 'Accès à l\'Annuaire (Entreprises)' },
      { key: 'annuaire_contacts_tab', label: 'Onglet Contacts (personnes)' },
      { key: 'annuaire_gel',          label: 'Geler / Dégeler une entreprise' },
      { key: 'annuaire_archive',      label: 'Archiver / Désarchiver une entreprise' },
      { key: 'annuaire_suppression',  label: 'Supprimer définitivement une entreprise' },
    ]
  },
  {
    key: 'distribution', label: 'Distribution',
    children: [
      { key: 'ventes',                label: 'Devis / Ventes' },
      { key: 'bons-livraison-ventes', label: 'Bons de livraison ventes' },
      { key: 'retours-ventes',        label: 'Bons de retour ventes' },
      { key: 'factures-distribution', label: 'Factures Distribution' },
      { key: 'commandes',             label: 'Commandes' },
      { key: 'facturation',           label: 'Facturation' },
    ]
  },
  {
    key: 'location_v2', label: 'Location',
    children: [
      { key: 'contrats_v2',     label: 'Contrats' },
      { key: 'planning',        label: 'Planning' },
      { key: 'controles',       label: 'BL avec contrôles' },
      { key: 'gammes_location', label: 'Gammes de location' },
    ]
  },
  {
    key: 'location', label: 'Location V1',
    children: [
      { key: 'contrats',        label: 'Contrats' },
      { key: 'livraisons',      label: 'Livraisons' },
      {
        key: 'facturation_loc', label: 'Facturation locations',
        children: [
          { key: 'facturation_loc_aide',     label: 'Onglet Aide à la facturation' },
          { key: 'facturation_loc_factures', label: 'Onglet Factures' },
        ]
      },
    ]
  },
  {
    key: 'atelier', label: 'Atelier',
    children: [
      { key: 'ordres_reparation',     label: 'Ordres de réparation' },
      { key: 'tableau_de_charge',     label: 'Tableau de charge' },
      { key: 'temps_non_facturables', label: 'Temps non facturables' },
      { key: 'plans_entretien',       label: 'Maintenance Préventive' },
      { key: 'atelier_factures',      label: 'Factures Atelier' },
    ]
  },
  {
    key: 'facturation_menu', label: 'Facturation',
    children: [
      { key: 'facturation_factures',   label: 'Factures client' },
      { key: 'facturation_reglements', label: 'Règlements clients' },
      { key: 'rapports_relances',      label: 'Relances' },
    ]
  },
  {
    key: 'achats', label: 'Achats',
    children: [
      { key: 'bons_achat',        label: "Commande d'achat" },
      { key: 'bons_reception',    label: 'Bons de réception' },
      { key: 'dossiers_revient',  label: 'Dossiers de revient' },
      { key: 'besoins_appro',     label: 'Approvisionnement (Reassort)' },
      { key: 'acomptes',          label: 'Acomptes / Virements SEPA' },
    ]
  },
  {
    key: 'materiels', label: 'Matériels',
    children: [
      { key: 'materiels_list',        label: 'Matériels' },
      { key: 'marques_modeles',       label: 'Marques & Modèles' },
      { key: 'financements',          label: 'Financements' },
      { key: 'controles_periodiques', label: 'Contrôles périodiques' },
      { key: 'comptages',             label: 'Comptages' },
    ]
  },
  {
    key: 'catalogue', label: 'Stocks',
    children: [
      { key: 'articles',          label: 'Articles' },
      { key: 'familles',          label: 'Familles' },
      { key: 'unites_contenants', label: 'Unités & Contenants' },
      { key: 'carac_techniques',  label: 'Caractéristiques techniques' },
      { key: 'stocks_liste',        label: 'Gestion des stocks' },
      { key: 'mouvements_zone',     label: 'Mouvements de stock (zones)' },
      { key: 'mouvements_stock',    label: 'Historique des mouvements' },
      { key: 'transferts',          label: 'Transferts inter-agences' },
      { key: 'stocks_valorisation', label: 'Valorisation des stocks' },
      {
        key: 'inventaires', label: 'Inventaires',
        children: [
          { key: 'inventaires_ajustements', label: 'Onglet Ajustements' },
          { key: 'inventaires_motifs',      label: 'Onglet Motifs de régul' },
        ]
      },
    ]
  },
  {
    key: 'rh', label: 'Ressources humaines',
    children: [
      { key: 'employes',              label: 'Employés' },
      { key: 'fonctions_internes',    label: 'Fonctions internes' },
    ]
  },
  {
    key: 'rapports', label: 'Rapports',
    children: [
      { key: 'rapports_materiels',         label: 'Matériels' },
      { key: 'rapports_potentiel_locatif', label: 'Potentiel locatif' },
      { key: 'rapports_balances_agees',    label: 'Balances âgées' },
      { key: 'rapports_palmares_clients',  label: 'Palmarès clients' },
      { key: 'rapports_previsions',        label: 'Prévisions trésorerie' },
    ]
  },
  {
    key: 'emailing', label: 'Emailing',
    children: [
      { key: 'email_campagnes', label: 'Campagnes' },
      { key: 'email_audiences', label: 'Audiences' },
      { key: 'email_modeles',   label: 'Modèles' },
      { key: 'email_logs',      label: 'Logs' },
    ]
  },
  { key: 'transfert_comptable', label: 'Transfert comptable' },
];

// ─── Section droits menu ──────────────────────────────────────────────────────

// Collecte récursivement toutes les clés de droits (enfants + petits-enfants)
function collectAllChildKeys(children) {
  return children.flatMap(c =>
    c.children ? [c.key, ...c.children.map(gc => gc.key)] : [c.key]
  );
}

function MenuRightsSection({ menuRights, onChange }) {
  const rights = menuRights || [];

  // Toutes les sections avec enfants sont repliées par défaut
  const initCollapsed = () =>
    Object.fromEntries(MENU_STRUCTURE.filter(s => s.children).map(s => [s.key, true]));
  const [collapsed, setCollapsed] = useState(initCollapsed);

  const isChecked = (key) => rights.includes(key);

  const toggle = (key) => {
    onChange(isChecked(key) ? rights.filter(k => k !== key) : [...rights, key]);
  };

  const toggleSection = (section) => {
    const allKeys = [section.key, ...collectAllChildKeys(section.children)];
    const allChecked = allKeys.every(k => rights.includes(k));
    if (allChecked) {
      onChange(rights.filter(k => !allKeys.includes(k)));
    } else {
      const toAdd = allKeys.filter(k => !rights.includes(k));
      onChange([...rights, ...toAdd]);
    }
  };

  // Toggle un child qui a lui-même des sub-enfants (page + onglets)
  const toggleChildWithSubs = (child) => {
    const allKeys = [child.key, ...child.children.map(gc => gc.key)];
    const allChecked = allKeys.every(k => rights.includes(k));
    if (allChecked) {
      onChange(rights.filter(k => !allKeys.includes(k)));
    } else {
      const toAdd = allKeys.filter(k => !rights.includes(k));
      onChange([...rights, ...toAdd]);
    }
  };

  // Toggle feuille : gère aussi la clé parente de section
  // Ajouter un enfant → ajoute aussi section.key ; retirer le dernier enfant → retire aussi section.key
  const toggleLeafChild = (childKey, section) => {
    const childChecked = rights.includes(childKey);
    if (childChecked) {
      const siblingsRemain = collectAllChildKeys(section.children)
        .some(k => k !== childKey && rights.includes(k));
      const toRemove = siblingsRemain ? [childKey] : [childKey, section.key];
      onChange(rights.filter(k => !toRemove.includes(k)));
    } else {
      const toAdd = [childKey, ...(rights.includes(section.key) ? [] : [section.key])];
      onChange([...rights, ...toAdd]);
    }
  };

  const selectAll = () => {
    const allKeys = MENU_STRUCTURE.flatMap(item =>
      item.children ? [item.key, ...collectAllChildKeys(item.children)] : [item.key]
    );
    onChange(allKeys);
  };

  const clearAll = () => onChange([]);

  const expandAll  = () => setCollapsed(Object.fromEntries(
    MENU_STRUCTURE.filter(s => s.children).map(s => [s.key, false])
  ));
  const collapseAll = () => setCollapsed(initCollapsed());

  const toggleCollapse = (key) =>
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="space-y-3">
      {/* Accès rapide */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={selectAll} data-testid="menu-rights-select-all"
          className="text-xs px-3 py-1.5 rounded-lg font-medium border transition-colors hover:bg-blue-50"
          style={{ borderColor: 'var(--primary-100)', color: 'var(--primary-solid)' }}>
          Tout accorder
        </button>
        <button onClick={clearAll} data-testid="menu-rights-clear-all"
          className="text-xs px-3 py-1.5 rounded-lg font-medium border transition-colors hover:bg-red-50"
          style={{ borderColor: 'var(--border-light)', color: 'var(--text-muted)' }}>
          Tout révoquer
        </button>
        <button onClick={expandAll} data-testid="menu-rights-expand-all"
          className="text-xs px-3 py-1.5 rounded-lg font-medium border transition-colors hover:bg-gray-50"
          style={{ borderColor: 'var(--border-light)', color: 'var(--text-secondary)' }}>
          Tout déplier
        </button>
        <button onClick={collapseAll}
          className="text-xs px-3 py-1.5 rounded-lg font-medium border transition-colors hover:bg-gray-50"
          style={{ borderColor: 'var(--border-light)', color: 'var(--text-secondary)' }}>
          Tout replier
        </button>
        <span className="ml-auto text-xs" style={{ color: 'var(--text-disabled)' }}>
          {rights.length} droit{rights.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Checklist */}
      <div className="space-y-2">
        {MENU_STRUCTURE.map(section => {
          if (!section.children) {
            const checked = isChecked(section.key);
            return (
              <label key={section.key}
                data-testid={`menu-right-${section.key}`}
                className="flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer select-none transition-colors"
                style={{
                  borderColor: checked ? 'var(--primary-100)' : 'var(--border-light)',
                  background: checked ? 'var(--primary-50)' : 'white'
                }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(section.key)}
                  className="w-4 h-4 rounded"
                  style={{ accentColor: 'var(--primary-solid)' }}
                />
                <span className="text-sm font-semibold"
                  style={{ color: checked ? 'var(--primary-solid)' : 'var(--text-primary)' }}>
                  {section.label}
                </span>
              </label>
            );
          }

          const allChildKeys = collectAllChildKeys(section.children);
          const checkedCount = allChildKeys.filter(k => rights.includes(k)).length;
          const allChecked = checkedCount === allChildKeys.length;
          const someChecked = checkedCount > 0 && !allChecked;
          const isCollapsed = collapsed[section.key] !== false;

          return (
            <div key={section.key}
              data-testid={`menu-section-${section.key}`}
              className="rounded-xl border overflow-hidden"
              style={{ borderColor: checkedCount > 0 ? 'var(--primary-100)' : 'var(--border-light)' }}>

              {/* Section header */}
              <div
                className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none hover:bg-gray-50/60"
                style={{ background: checkedCount > 0 ? 'var(--primary-50)' : 'white' }}
                onClick={() => toggleCollapse(section.key)}>
                <input
                  type="checkbox"
                  checked={allChecked}
                  ref={el => { if (el) el.indeterminate = someChecked; }}
                  onChange={() => toggleSection(section)}
                  onClick={e => e.stopPropagation()}
                  className="w-4 h-4 rounded"
                  style={{ accentColor: 'var(--primary-solid)' }}
                />
                <span className="font-semibold text-sm flex-1"
                  style={{ color: checkedCount > 0 ? 'var(--primary-solid)' : 'var(--text-primary)' }}>
                  {section.label}
                </span>
                {checkedCount > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded-full"
                    style={{ background: 'var(--primary-100)', color: 'var(--primary-solid)' }}>
                    {checkedCount}/{allChildKeys.length}
                  </span>
                )}
                <ChevronRight size={14}
                  className="transition-transform duration-200 flex-shrink-0"
                  style={{
                    transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                    color: 'var(--text-muted)'
                  }} />
              </div>

              {/* Children */}
              {!isCollapsed && (
                <div className="border-t px-4 py-2 space-y-0.5"
                  style={{ borderColor: 'var(--border-light)', background: 'white' }}>
                  {section.children.map(child => {
                    const childChecked = isChecked(child.key);

                    // Child avec sous-onglets (3ème niveau)
                    if (child.children) {
                      const subKeys = child.children.map(gc => gc.key);
                      const subCheckedCount = subKeys.filter(k => rights.includes(k)).length;
                      const allSubChecked = childChecked && subCheckedCount === subKeys.length;
                      const someSubChecked = (childChecked || subCheckedCount > 0) && !allSubChecked;

                      return (
                        <div key={child.key}
                          data-testid={`menu-right-${child.key}`}
                          className="rounded-lg border overflow-hidden mb-1"
                          style={{ borderColor: childChecked || subCheckedCount > 0 ? 'var(--primary-100)' : 'var(--border-light)' }}>

                          {/* Page header */}
                          <label
                            className="flex items-center gap-3 px-3 py-2 cursor-pointer select-none hover:bg-gray-50/40 transition-colors"
                            style={{ background: childChecked ? 'var(--primary-50)' : 'white' }}>
                            <input
                              type="checkbox"
                              checked={allSubChecked}
                              ref={el => { if (el) el.indeterminate = someSubChecked; }}
                              onChange={() => toggleChildWithSubs(child)}
                              onClick={e => e.stopPropagation()}
                              className="w-3.5 h-3.5 rounded"
                              style={{ accentColor: 'var(--primary-solid)' }}
                            />
                            <span className="text-sm font-medium flex-1"
                              style={{ color: childChecked ? 'var(--primary-solid)' : 'var(--text-secondary)' }}>
                              {child.label}
                            </span>
                            {subCheckedCount > 0 && (
                              <span className="text-xs px-1.5 py-0.5 rounded-full"
                                style={{ background: 'var(--primary-100)', color: 'var(--primary-solid)', fontSize: 10 }}>
                                +{subCheckedCount} onglet{subCheckedCount > 1 ? 's' : ''}
                              </span>
                            )}
                          </label>

                          {/* Sous-onglets */}
                          <div className="border-t pl-8 pr-3 py-1.5 space-y-0.5"
                            style={{ borderColor: 'var(--border-light)', background: '#FAFAFA' }}>
                            {/* Page access checkbox */}
                            <label
                              className="flex items-center gap-2 px-2 py-1 rounded cursor-pointer select-none hover:bg-gray-50 transition-colors">
                              <input
                                type="checkbox"
                                checked={childChecked}
                                onChange={() => toggle(child.key)}
                                className="w-3 h-3 rounded"
                                style={{ accentColor: 'var(--primary-solid)' }}
                              />
                              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                Accès à la page
                              </span>
                            </label>
                            {child.children.map(gc => {
                              const gcChecked = isChecked(gc.key);
                              return (
                                <label key={gc.key}
                                  data-testid={`menu-right-${gc.key}`}
                                  className="flex items-center gap-2 px-2 py-1 rounded cursor-pointer select-none hover:bg-gray-50 transition-colors"
                                  style={{ background: gcChecked ? '#EFF6FF' : 'transparent' }}>
                                  <input
                                    type="checkbox"
                                    checked={gcChecked}
                                    onChange={() => toggle(gc.key)}
                                    className="w-3 h-3 rounded"
                                    style={{ accentColor: 'var(--primary-solid)' }}
                                  />
                                  <span className="text-xs"
                                    style={{ color: gcChecked ? 'var(--primary-solid)' : 'var(--text-muted)' }}>
                                    {gc.label}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }

                    // Child simple (feuille)
                    return (
                      <label key={child.key}
                        data-testid={`menu-right-${child.key}`}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer select-none hover:bg-gray-50/50 transition-colors"
                        style={{ background: childChecked ? 'var(--primary-50)' : 'transparent' }}>
                        <input
                          type="checkbox"
                          checked={childChecked}
                          onChange={() => toggleLeafChild(child.key, section)}
                          className="w-3.5 h-3.5 rounded"
                          style={{ accentColor: 'var(--primary-solid)' }}
                        />
                        <span className="text-sm"
                          style={{ color: childChecked ? 'var(--primary-solid)' : 'var(--text-secondary)' }}>
                          {child.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Accès Terrain — DÉPLACÉ dans l'onglet Terrain */}
    </div>
  );
}


// ─── Section droits terrain ───────────────────────────────────────────────────

const TERRAIN_MODULES = [
  {
    key: 'terrain_atelier',
    title: 'Mes interventions (atelier)',
    description: 'Consulter les ordres de réparation affectés et pointer les heures depuis le mobile (/terrain/atelier)',
    icon: Wrench,
  },
  {
    key: 'terrain_comptages',
    title: 'Comptages matériels',
    description: 'Réaliser des sessions de comptage de matériels depuis la tablette (/terrain/comptages)',
    icon: Tablet,
  },
  {
    key: 'terrain_contrats_location',
    title: 'Contrats à signer',
    description: 'Consulter les contrats en attente de signature et signer sur tablette (/terrain/signatures)',
    icon: Send,
  },
  {
    key: 'terrain_bons_livraison',
    title: 'Bons de livraison/retour de location',
    description: 'Réaliser les contrôles de matériels et signer les bons de livraison et de retour (/terrain/bons-livraison)',
    icon: Tablet,
  },
  {
    key: 'terrain_commandes_vente',
    title: 'Commandes de vente',
    description: 'Signer les devis et confirmer les commandes de vente depuis la tablette (/terrain/commandes-vente)',
    icon: ShoppingCart,
  },
];

function TerrainRightsSection({ rights, onChange }) {
  const safeRights = rights || [];
  const toggle = (key) =>
    onChange(safeRights.includes(key) ? safeRights.filter(r => r !== key) : [...safeRights, key]);

  return (
    <div className="space-y-3">
      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
        Sélectionnez les modules terrain accessibles depuis l'interface tablette
        (URL : <span className="font-mono text-xs">/terrain</span>).
      </p>
      {TERRAIN_MODULES.map(mod => {
        const active = safeRights.includes(mod.key);
        const Icon = mod.icon;
        return (
          <label key={mod.key}
            data-testid={`terrain-right-${mod.key}`}
            className="flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors"
            style={{ borderColor: active ? '#3B82F6' : 'var(--border-light)', background: active ? '#EFF6FF' : 'white' }}>
            <input type="checkbox" checked={active} onChange={() => toggle(mod.key)}
              className="w-4 h-4 mt-0.5 rounded flex-shrink-0" style={{ accentColor: '#3B82F6' }} />
            <Icon size={15} className="mt-0.5 flex-shrink-0"
              style={{ color: active ? '#3B82F6' : 'var(--text-muted)' }} />
            <div>
              <p className="text-sm font-semibold" style={{ color: '#111827' }}>{mod.title}</p>
              <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>{mod.description}</p>
            </div>
          </label>
        );
      })}
    </div>
  );
}


// ─── SidePanel fiche utilisateur ──────────────────────────────────────────────

function UserPanel({ u, me, societes, onClose, onUpdated, onDeleted }) {
  const isMe = u.user_id === me?.user_id;
  const [activeTab, setActiveTab] = useState('profil');
  const [name, setName] = useState(u.name || '');
  const [role, setRole] = useState(u.role);
  const [actif, setActif] = useState(u.actif);
  const [employeId, setEmployeId] = useState(u.employe_id || '');
  const [employes, setEmployes] = useState([]);
  const [access, setAccess] = useState([]);
  const [menuRights, setMenuRights] = useState(null);
  const [loadingAccess, setLoadingAccess] = useState(true);
  const [saving, setSaving]             = useState(false);
  // État local pour le statut d'invitation (mis à jour sans fermer le panneau)
  const [invitationPending, setInvitationPending]       = useState(u.invitation_pending);
  const [invitationExpiresAt, setInvitationExpiresAt]   = useState(u.invitation_expires_at);
  // Connexions (super-admin uniquement)
  const [connexions, setConnexions]     = useState([]);
  const [loadingCx, setLoadingCx]       = useState(false);

  useEffect(() => {
    Promise.all([
      api.getUserAccess(u.user_id),
      api.getUserMenuRights(u.user_id),
      api.getEmployes({ limit: 200 }),
    ])
      .then(([accessRes, rightsRes, empRes]) => {
        const rawAccess = accessRes.data.access;
        setAccess(Array.isArray(rawAccess) ? rawAccess : []);
        setMenuRights(rightsRes.data.menu_rights ?? []);
        const items = Array.isArray(empRes.data) ? empRes.data : (empRes.data?.items || []);
        setEmployes(items);
      })
      .catch(() => { setAccess([]); setMenuRights(null); })
      .finally(() => setLoadingAccess(false));
  }, [u.user_id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Mettre à jour le nom si changé
      if (name.trim() !== u.name) {
        await api.updateUserName(u.user_id, name.trim());
      }
      // Mettre à jour le rôle si changé
      if (role !== u.role && !isMe) {
        await api.updateUserRole(u.user_id, role);
      }
      // Mettre à jour le statut si changé
      if (actif !== u.actif && !isMe) {
        await api.toggleUserStatut(u.user_id, actif);
      }
      // Mettre à jour les accès sociétés/agences
      await api.updateUserAccess(u.user_id, Array.isArray(access) ? access : []);
      // Mettre à jour le lien employé si changé
      if (employeId !== (u.employe_id || '')) {
        await api.updateUserEmployeId(u.user_id, employeId || null);
      }
      // Mettre à jour les droits menu
      await api.updateUserMenuRights(u.user_id, Array.isArray(menuRights) ? menuRights : []);

      onUpdated({ ...u, name: name.trim(), role, actif, employe_id: employeId || null });
      toast.success('Fiche mise à jour');
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d.msg || String(d)).join(', ')
        : (detail || 'Erreur lors de la sauvegarde');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleResendInvite = async () => {
    setSaving(true);
    try {
      const res = await api.resendInvite(u.user_id, { app_url: window.location.origin });
      const wasNeverSent = !invitationPending;
      // Mettre à jour le statut LOCAL (panneau reste ouvert pour montrer la confirmation)
      setInvitationPending(true);
      setInvitationExpiresAt(res.data.expires_at);
      // Mettre à jour la liste (tableau en arrière-plan)
      onUpdated({ ...u, invitation_pending: true, invitation_expires_at: res.data.expires_at });
      toast.success(wasNeverSent
        ? 'Invitation envoyée ! Valable 7 jours.'
        : 'Invitation renvoyée ! Valable 7 jours.'
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer ${u.name} définitivement ?`)) return;
    try {
      await api.deleteUser(u.user_id);
      onDeleted(u.user_id);
      toast.success('Utilisateur supprimé');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const isAdminRole = u.role === 'admin_org' || u.role === 'super_admin';
  const initials = (u.name || '').split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
  const inputCls = `w-full px-3 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-200`;
  const inputStyle = { borderColor: 'var(--border-light)', background: 'white', color: 'var(--text-primary)' };
  const isSuperAdmin = me?.role === 'super_admin';

  const TABS = [
    { key: 'profil',     label: 'Profil' },
    { key: 'acces',      label: 'Accès' },
    { key: 'droits',     label: 'Droits Menu' },
    { key: 'terrain',    label: 'Terrain' },
    ...(isSuperAdmin ? [{ key: 'connexions', label: 'Connexions' }] : []),
  ];

  // Charger les connexions quand l'onglet devient actif
  const prevTab = React.useRef(null);
  useEffect(() => {
    if (activeTab === 'connexions' && prevTab.current !== 'connexions' && isSuperAdmin) {
      setLoadingCx(true);
      api.getUserConnexions(u.user_id)
        .then(r => setConnexions(r.data || []))
        .catch(() => setConnexions([]))
        .finally(() => setLoadingCx(false));
    }
    prevTab.current = activeTab;
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <SidePanel
      open
      onOpenChange={open => { if (!open) onClose(); }}
      title="Fiche utilisateur"
      size="lg"
      footer={
        <div className="flex items-center justify-between">
          {!isMe ? (
            <button onClick={handleDelete} data-testid="user-panel-delete-btn"
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium"
              style={{ color: '#DC2626' }}>
              <Trash2 size={14} /> Supprimer
            </button>
          ) : <span />}
          <div className="flex gap-3">
            <button onClick={onClose}
              className="px-4 py-2.5 rounded-lg text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
              Annuler
            </button>
            <button onClick={handleSave} disabled={saving} data-testid="user-panel-save-btn"
              className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-60"
              style={{ background: 'var(--primary-gradient)' }}>
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">

        {/* Identité */}
        <div className="flex items-center gap-4 p-4 rounded-2xl"
          style={{ background: 'var(--surface-1)', border: '1px solid var(--border-light)' }}>
          <div className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold text-white flex-shrink-0"
            style={{ background: 'var(--primary-gradient)' }}>
            {initials}
          </div>
          <div>
            <div className="font-bold text-base flex items-center gap-2"
              style={{  color: 'var(--text-primary)' }}>
              {u.name}
              {isMe && <span className="text-xs px-2 py-0.5 rounded-full font-normal"
                style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>Vous</span>}
            </div>
            <div className="text-sm" style={{ color: 'var(--text-muted)' }}>{u.email}</div>
            <div className="mt-1.5">
              <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                u.actif ? 'text-green-700 bg-green-50' : 'text-red-600 bg-red-50'
              }`}>
                {u.actif ? <CheckCircle size={10} /> : <XCircle size={10} />}
                {u.actif ? 'Actif' : 'Inactif'}
              </span>
            </div>
          </div>
        </div>

        {/* Onglets */}
        <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'var(--surface-1)' }}>
          {TABS.map(tab => (
            <button
              key={tab.key}
              data-testid={`user-tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className="flex-1 py-2 text-xs font-semibold rounded-lg transition-all"
              style={{
                background: activeTab === tab.key ? 'white' : 'transparent',
                color: activeTab === tab.key ? 'var(--primary-solid)' : 'var(--text-muted)',
                boxShadow: activeTab === tab.key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Onglet Profil */}
        {activeTab === 'profil' && (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide"
                style={{ color: 'var(--text-muted)' }}>Nom complet</label>
              <input
                data-testid="user-panel-name-input"
                className={inputCls}
                style={inputStyle}
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Prénom Nom"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide"
                style={{ color: 'var(--text-muted)' }}>Rôle</label>
              <select
                data-testid="user-panel-role-select"
                className={`${inputCls} appearance-none`}
                style={{ ...inputStyle, ...(isMe ? { background: 'var(--surface-1)', cursor: 'not-allowed' } : {}) }}
                value={role}
                disabled={isMe}
                onChange={e => setRole(e.target.value)}>
                {Object.entries(ROLES).map(([r, cfg]) => (
                  <option key={r} value={r}>{cfg.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide"
                style={{ color: 'var(--text-muted)' }}>Statut</label>
              <select
                data-testid="user-panel-statut-select"
                className={`${inputCls} appearance-none`}
                style={{ ...inputStyle, ...(isMe ? { background: 'var(--surface-1)', cursor: 'not-allowed' } : {}) }}
                value={actif ? 'actif' : 'inactif'}
                disabled={isMe}
                onChange={e => setActif(e.target.value === 'actif')}>
                <option value="actif">Actif</option>
                <option value="inactif">Inactif</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide"
                style={{ color: 'var(--text-muted)' }}>Employé lié (terrain)</label>
              <select
                data-testid="user-panel-employe-select"
                className={`${inputCls} appearance-none`}
                style={inputStyle}
                value={employeId}
                onChange={e => setEmployeId(e.target.value)}>
                <option value="">— Aucun —</option>
                {employes.filter(e => e.statut === 'actif').map(e => {
                  const nom = e.personne ? `${e.personne.prenom || ''} ${e.personne.nom || ''}`.trim() : e.employe_id;
                  return <option key={e.employe_id} value={e.employe_id}>{nom}</option>;
                })}
              </select>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                Permet l'accès à "Mes interventions" en mode terrain.
              </p>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide"
                style={{ color: 'var(--text-muted)' }}>Membre depuis</label>
              <input className={inputCls}
                style={{ ...inputStyle, background: 'var(--surface-1)', cursor: 'default' }}
                value={u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
                readOnly />
            </div>

            {/* Bloc invitation — en attente ou jamais envoyée */}
            {!isMe && !u.email_verified && (invitationPending || u.invited_by) && (() => {
              const neverSent = !invitationPending;
              const expired = !neverSent && invitationExpiresAt && new Date(invitationExpiresAt) < new Date();
              const expiresLabel = invitationExpiresAt
                ? new Date(invitationExpiresAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
                : null;
              const bg = neverSent ? '#F0F9FF' : expired ? '#FEF2F2' : '#FFFBEB';
              const borderColor = neverSent ? '#BAE6FD' : expired ? '#FECACA' : '#FDE68A';
              const textColor = neverSent ? '#0369A1' : expired ? '#DC2626' : '#92400E';
              const subColor = neverSent ? '#0284C7' : expired ? '#DC2626' : '#B45309';
              return (
                <div className="col-span-2 rounded-xl p-3 flex items-center justify-between gap-3"
                  style={{ background: bg, border: `1px solid ${borderColor}` }}>
                  <div>
                    <p className="text-xs font-semibold flex items-center gap-1.5" style={{ color: textColor }}>
                      {neverSent ? <Mail size={12} /> : <Clock size={12} />}
                      {neverSent ? 'Invitation non envoyée' : expired ? 'Invitation expirée' : 'Invitation en attente'}
                    </p>
                    {!neverSent && expiresLabel && (
                      <p className="text-xs mt-0.5" style={{ color: subColor }}>
                        {expired ? `Expirée le ${expiresLabel}` : `Expire le ${expiresLabel}`}
                      </p>
                    )}
                    {neverSent && (
                      <p className="text-xs mt-0.5" style={{ color: subColor }}>
                        Aucun email d'invitation envoyé
                      </p>
                    )}
                  </div>
                  <button type="button"
                    data-testid={neverSent ? 'send-invite-btn' : 'resend-invite-btn'}
                    onClick={handleResendInvite}
                    disabled={saving}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white flex-shrink-0 disabled:opacity-50"
                    style={{ background: 'var(--primary-gradient)' }}>
                    <Send size={12} /> {neverSent ? 'Envoyer' : 'Renvoyer'}
                  </button>
                </div>
              );
            })()}
          </div>
        )}

        {/* Onglet Accès sociétés/agences */}
        {activeTab === 'acces' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold uppercase tracking-widest"
                style={{ color: 'var(--text-muted)' }}>Accès sociétés & agences</p>
              {access.length === 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full"
                  style={{ background: '#fef3c7', color: '#92400e' }}>
                  Accès global
                </span>
              )}
            </div>

            {access.length === 0 && (
              <div className="flex items-start gap-2 p-3 rounded-xl mb-3 text-xs"
                style={{ background: '#fef9ee', border: '1px solid #fde68a', color: '#78350f' }}>
                <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                Sans restriction définie, cet utilisateur a accès à toutes les sociétés et agences.
              </div>
            )}

            {loadingAccess ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-t-transparent"
                  style={{ borderColor: 'var(--primary-solid)' }} />
              </div>
            ) : (
              <AccessSection societes={societes} access={access} onChange={setAccess} />
            )}
          </div>
        )}

        {/* Onglet Droits Menu */}
        {activeTab === 'droits' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold uppercase tracking-widest"
                style={{ color: 'var(--text-muted)' }}>Visibilité du menu</p>
            </div>

            {isAdminRole ? (
              <div className="flex items-start gap-2 p-3 rounded-xl text-xs"
                style={{ background: 'var(--primary-50)', border: '1px solid var(--primary-100)', color: 'var(--primary-solid)' }}>
                <Lock size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                Les administrateurs voient toujours tout le menu, quels que soient les droits configurés ici.
              </div>
            ) : loadingAccess ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-t-transparent"
                  style={{ borderColor: 'var(--primary-solid)' }} />
              </div>
            ) : (
              <MenuRightsSection menuRights={menuRights} onChange={setMenuRights} />
            )}
          </div>
        )}

        {/* Onglet Terrain */}
        {activeTab === 'terrain' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold uppercase tracking-widest"
                style={{ color: 'var(--text-muted)' }}>Accès Terrain (Tablette)</p>
              <span className="text-xs px-2 py-0.5 rounded-full font-semibold flex items-center gap-1"
                style={{ background: '#EFF6FF', color: '#3B82F6' }}>
                <Tablet size={10} /> Tablette
              </span>
            </div>
            {isAdminRole ? (
              <div className="flex items-start gap-2 p-3 rounded-xl text-xs"
                style={{ background: 'var(--primary-50)', border: '1px solid var(--primary-100)', color: 'var(--primary-solid)' }}>
                <Lock size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                Les administrateurs ont accès à tous les modules terrain par défaut.
              </div>
            ) : (
              <TerrainRightsSection rights={menuRights} onChange={setMenuRights} />
            )}
          </div>
        )}

        {/* Onglet Connexions */}
        {activeTab === 'connexions' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
                Historique des connexions <span style={{ fontWeight: 400 }}>(30 derniers jours)</span>
              </p>
              <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                style={{ background: '#F3F4F6', color: 'var(--text-muted)' }}>
                {connexions.length} entrée{connexions.length !== 1 ? 's' : ''}
              </span>
            </div>
            {loadingCx ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-t-transparent"
                  style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
              </div>
            ) : connexions.length === 0 ? (
              <div className="flex flex-col items-center py-10 text-center rounded-xl border"
                style={{ borderColor: 'var(--border-light)', background: '#FAFAFA' }}>
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aucune connexion enregistrée</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-placeholder)' }}>
                  Les connexions sont enregistrées à partir de maintenant
                </p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {connexions.map((cx, i) => {
                  const dt = new Date(cx.login_at);
                  const date = dt.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
                  const heure = dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                  return (
                    <div key={i} data-testid={`connexion-row-${i}`}
                      className="flex items-center justify-between px-3 py-2.5 rounded-xl"
                      style={{ background: i === 0 ? 'var(--primary-50)' : '#F9FAFB', border: '1px solid var(--border-light)' }}>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)',  }}>
                            {date}
                          </span>
                          <span className="text-xs font-mono px-1.5 py-0.5 rounded"
                            style={{ background: 'var(--border-light)', color: 'var(--text-secondary)' }}>
                            {heure}
                          </span>
                          {i === 0 && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold"
                              style={{ background: 'var(--primary-100)', color: 'var(--primary-solid)' }}>
                              Dernière
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 text-xs" style={{ color: 'var(--text-muted)' }}>
                          <span>{cx.navigateur || '—'}</span>
                          {cx.ip && cx.ip !== 'unknown' && (
                            <><span>·</span><span className="font-mono">{cx.ip}</span></>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

      </div>
    </SidePanel>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function Utilisateurs() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [societes, setSocietes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const load = useCallback(() => {
    Promise.all([api.getUsers(), api.getSocietes()])
      .then(([usersRes, socRes]) => {
        setUsers(usersRes.data || []);
        setSocietes(socRes.data || []);
      })
      .catch(() => toast.error('Erreur lors du chargement'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const onCreated = (newUser) => setUsers(us => [...us, newUser]);

  const onUpdated = (updated) => {
    setUsers(us => us.map(u => u.user_id === updated.user_id ? { ...u, ...updated } : u));
    setSelectedUser(prev => prev && prev.user_id === updated.user_id ? { ...prev, ...updated } : prev);
  };

  const onDeleted = (userId) => setUsers(us => us.filter(u => u.user_id !== userId));

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 rounded-xl" style={{ background: 'var(--primary-50)' }}>
              <Users size={20} style={{ color: 'var(--primary-solid)' }} />
            </div>
            <h1 className="text-xl font-bold" style={{  color: 'var(--text-primary)' }}>
              Utilisateurs
            </h1>
          </div>
          <p className="text-sm ml-11" style={{ color: 'var(--text-muted)' }}>
            {users.length} membre{users.length > 1 ? 's' : ''} dans votre organisation
          </p>
        </div>
        <button onClick={() => setShowInvite(true)} data-testid="invite-user-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white"
          style={{ background: 'var(--primary-gradient)' }}>
          <UserPlus size={15} /> Créer un utilisateur
        </button>
      </div>

      {/* Table */}
      <div className="rounded-2xl border overflow-hidden"
        style={{ borderColor: 'var(--border-light)', background: 'white' }}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-t-transparent"
              style={{ borderColor: 'var(--primary-solid)' }} />
          </div>
        ) : users.length === 0 ? (
          <div className="py-16 text-center" style={{ color: 'var(--text-muted)' }}>
            <Users size={36} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">Aucun utilisateur</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b" style={{ borderColor: 'var(--border-light)', background: 'var(--surface-1)' }}>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wide"
                  style={{ color: 'var(--text-muted)' }}>Utilisateur</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide"
                  style={{ color: 'var(--text-muted)' }}>Rôle</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide"
                  style={{ color: 'var(--text-muted)' }}>Statut</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide"
                  style={{ color: 'var(--text-muted)' }}>Depuis</th>
                <th className="px-4 py-3 w-8" />
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => {
                const isMe = u.user_id === me?.user_id;
                return (
                  <tr key={u.user_id}
                    data-testid={`user-row-${u.user_id}`}
                    onClick={() => setSelectedUser(u)}
                    className="border-b last:border-b-0 hover:bg-blue-50/40 transition-colors cursor-pointer"
                    style={{ borderColor: 'var(--border-light)', opacity: u.actif ? 1 : 0.55 }}>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                          style={{ background: `hsl(${(i * 47) % 360}, 60%, 55%)` }}>
                          {u.name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5 font-medium" style={{ color: 'var(--text-primary)' }}>
                            {u.name}
                            {isMe && <span className="text-xs px-1.5 py-0.5 rounded-full font-normal"
                              style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>Vous</span>}
                          </div>
                          <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5"><RoleBadge role={u.role} /></td>
                    <td className="px-4 py-3.5">
                      {!u.email_verified && !u.invitation_pending && u.actif && u.invited_by ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full text-sky-700 bg-sky-50">
                          <Mail size={10} /> Non invité
                        </span>
                      ) : u.invitation_pending && u.actif ? (
                        (() => {
                          const expired = u.invitation_expires_at && new Date(u.invitation_expires_at) < new Date();
                          return (
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                              expired ? 'text-red-600 bg-red-50' : 'text-amber-700 bg-amber-50'
                            }`}>
                              <Clock size={10} />
                              {expired ? 'Invitation expirée' : 'Invitation en attente'}
                            </span>
                          );
                        })()
                      ) : (
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                          u.actif ? 'text-green-700 bg-green-50' : 'text-red-600 bg-red-50'
                        }`}>
                          {u.actif ? <CheckCircle size={10} /> : <XCircle size={10} />}
                          {u.actif ? 'Actif' : 'Inactif'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-xs" style={{ color: 'var(--text-muted)' }}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : '—'}
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <ChevronRight size={14} style={{ color: 'var(--text-disabled)' }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-xs text-center" style={{ color: 'var(--text-disabled)' }}>
        Les invitations sont valables {7} jours.
      </p>

      {/* Side panel fiche utilisateur */}
      {selectedUser && (
        <UserPanel
          u={selectedUser}
          me={me}
          societes={societes}
          onClose={() => setSelectedUser(null)}
          onUpdated={onUpdated}
          onDeleted={onDeleted}
        />
      )}

      {/* Side panel création utilisateur */}
      {showInvite && (
        <CreateUserPanel onClose={() => setShowInvite(false)} onCreated={onCreated} societes={societes} />
      )}
    </div>
  );
}
